import React from "react";
import JsonView from '@uiw/react-json-view';
import { lightTheme } from '@uiw/react-json-view/light';

interface JsonViewerProps {
  /**
   * The JSON data to display
   */
  data: any;

  /**
   * Whether to start with all nodes collapsed (default: false - all expanded)
   */
  collapsed?: boolean | number;

  /**
   * Maximum height of the viewer (default: '70vh')
   */
  maxHeight?: string;

  /**
   * Font size (default: '13px')
   */
  fontSize?: string;

  /**
   * Line height (default: '1.6')
   */
  lineHeight?: string;

  /**
   * Whether to enable clipboard copy functionality (default: true)
   */
  enableClipboard?: boolean;

  /**
   * Whether to show array indices (default: false)
   */
  showArrayKeys?: boolean;

  /**
   * Background color (default: '#fafafa')
   */
  backgroundColor?: string;

  /**
   * Border color (default: '#e8e8e8')
   */
  borderColor?: string;

  /**
   * Padding (default: '0px')
   */
  padding?: string;
}

/**
 * A clean, reusable JSON viewer component with syntax highlighting.
 *
 * Features:
 * - Syntax highlighting with beautiful colors
 * - Collapsible nested objects/arrays
 * - Copy to clipboard functionality
 * - Mobile-friendly with text wrapping
 * - Performance optimized with memoization
 * - Fully customizable appearance
 *
 * @example
 * ```tsx
 * <JsonViewer data={myData} />
 * <JsonViewer data={myData} collapsed={1} maxHeight="50vh" />
 * <JsonViewer data={myData} fontSize="16px" backgroundColor="#ffffff" />
 * ```
 */
const JsonViewer = React.memo(
  function JsonViewer({
    data,
    collapsed = false,
    maxHeight = '70vh',
    fontSize = '18px',
    lineHeight = '2',
    enableClipboard = true,
    showArrayKeys = false,
    backgroundColor = '#fafafa',
    borderColor = '#e8e8e8',
    padding = '0px'
  }: JsonViewerProps) {

    return (
      <div style={{
        width: '100%',
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize,
        borderRadius: '4px',
        border: `1px solid ${borderColor}`,
        padding,
        backgroundColor,
        boxShadow: 'none',
        WebkitOverflowScrolling: 'touch'
      }}>
        <JsonView
          value={data}
          collapsed={collapsed}
          displayDataTypes={false}
          displayObjectSize={false}
          enableClipboard={enableClipboard}
          shortenTextAfterLength={0}
          // keyName={showArrayKeys}
          style={{
            ...lightTheme,
            '--w-rjv-font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            '--w-rjv-line-height': lineHeight,
            '--w-rjv-font-size': fontSize,
            '--w-rjv-padding': '6px 0',
            '--w-rjv-indent-width': '24px',
            '--w-rjv-background-color': '#ffffff',
            '--w-rjv-border-radius': '4px',
            '--w-rjv-key-string': '#1a73e8',
            '--w-rjv-value-string': '#137333',
            '--w-rjv-value-number': '#c5221f',
            '--w-rjv-value-boolean': '#ea8600',
            '--w-rjv-value-null': '#757575',
            '--w-rjv-quotes': '#137333',
            '--w-rjv-arrow-color': '#202124',
            '--w-rjv-info-color': '#5f6368',
            '--w-rjv-bracket-color': '#202124',
            '--w-rjv-copied-color': '#137333',
            '--w-rjv-copied-success-color': '#137333',
            '--w-rjv-cursor-pointer': 'pointer',
            'fontWeight': '400',
            'fontSize': fontSize,
            'wordBreak': 'break-word',
            'overflowWrap': 'break-word',
            'whiteSpace': 'pre-wrap',
            'lineHeight': lineHeight,
          } as React.CSSProperties}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if data or key display options change
    return (
      JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
      prevProps.collapsed === nextProps.collapsed &&
      prevProps.maxHeight === nextProps.maxHeight &&
      prevProps.fontSize === nextProps.fontSize &&
      prevProps.lineHeight === nextProps.lineHeight &&
      prevProps.enableClipboard === nextProps.enableClipboard &&
      prevProps.showArrayKeys === nextProps.showArrayKeys &&
      prevProps.backgroundColor === nextProps.backgroundColor &&
      prevProps.borderColor === nextProps.borderColor &&
      prevProps.padding === nextProps.padding
    );
  }
);

export default JsonViewer;